import { ComponentPropsWithoutRef, ComponentPropsWithRef, ElementType, forwardRef, ReactElement } from 'react';
import { styled } from '@mui/material';
import MenuItem, { MenuItemProps } from '@o2/ui/MenuItem';
import { CheckMarkFill, ChevronRight } from '@o2/icons';
import { shouldForwardProp as sysShouldForwardProp } from '@mui/system';
import { DisplayNameObj } from '@o2/ui/theme/types';

export type DropdownItemProps<T extends ElementType = 'button'> = {
  active?: boolean;

  /**
   * What component to render this component as (inheriting props).
   * Useful when combining with e.g. react-router.
   */
  as?: T;

  ref?: ComponentPropsWithRef<T>['ref'];
} & ComponentPropsWithoutRef<T> &
  Omit<MenuItemProps, 'ref'>;

const StyledMenuItem = styled(MenuItem, {
  name: 'AppBarInternal',
  slot: 'DropDownItem',
  shouldForwardProp: (prop) => sysShouldForwardProp(prop) && prop !== 'active',
})<DropdownItemProps>(({ theme, active }) => ({
  display: 'flex',
  flexDirection: 'row',
  whiteSpace: 'nowrap',
  alignItems: 'center',
  gap: 4,
  width: '100%',
  minWidth: '150px',
  padding: theme.spacing(1, 2),
  fontFamily: theme.typography.fontFamily,
  fontSize: 14,
  textDecoration: 'none',
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  borderBottom: `1px solid ${theme.palette.divider}`,
  color: theme.palette.text.primary,
  fontWeight: 400,
  '&:hover': {
    background: theme.palette.background.primary,
    border: 'none',
    borderBottom: `1px solid ${theme.palette.divider}`,
    color: theme.palette.common.black,
  },
  '&:last-of-type': {
    borderBottom: 'none',
  },
  '& .MuiSvgIcon-root': {
    marginLeft: theme.spacing(1),
    color: theme.palette.background.grayTransparent,
  },
  ...(active && {
    fontWeight: 600,
    backgroundColor: theme.palette.background.success,
    '& .MuiSvgIcon-root': {
      color: theme.palette.success.main,
    },
  }),
}));

const DropDownItem: (<T extends ElementType = 'button'>(props: DropdownItemProps<T>) => ReactElement | null) &
  DisplayNameObj = forwardRef(
  <T extends ElementType = 'button'>(props: DropdownItemProps<T>, ref: DropdownItemProps<T>['ref']) => {
    const { active, ...other } = props;
    const icon = active ? <CheckMarkFill fontSize="small" /> : <ChevronRight fontSize="small" />;

    return <StyledMenuItem active={active} endIcon={icon} ref={ref} {...other} />;
  }
);

DropDownItem.displayName = 'AppBarInternal.DropDownItem';

export default DropDownItem;
